import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { View } from '../types';

interface UIContextType {
  currentView: View | 'admin';
  setCurrentView: (view: View | 'admin') => void;
  activeWorkspace: any;
  setActiveWorkspace: (ws: any) => void;
  workspaces: any[];
  setWorkspaces: (ws: any[]) => void;
  user: any;
  setUser: (u: any) => void;
  dbUser: any;
  setDbUser: (u: any) => void;
  loading: boolean;
  globalFilters: { search: string; dateRange: string; status: string };
  setGlobalFilters: React.Dispatch<React.SetStateAction<{ search: string; dateRange: string; status: string }>>;
  isCreateWorkspaceModalOpen: boolean;
  setIsCreateWorkspaceModalOpen: (open: boolean) => void;
  toastMessage: string | null;
  showToast: (msg: string, type?: string) => void;
  theme: 'light' | 'dark';
  toggleTheme: () => void;
  teamMembers: any[];
}

const UIContext = createContext<UIContextType | null>(null);

export function UIProvider({ children }: { children: React.ReactNode }) {
  const [currentView, setCurrentView] = useState<View | 'admin'>('dashboard');
  const [workspaces, setWorkspaces] = useState<any[]>([]);
  const [activeWorkspace, setActiveWorkspace] = useState<any>(null);
  const [user, setUser] = useState<any>(null);
  const [dbUser, setDbUser] = useState<any>(null);
  const [loading] = useState(false);
  const [globalFilters, setGlobalFilters] = useState({ search: '', dateRange: 'all', status: 'all' });
  const [isCreateWorkspaceModalOpen, setIsCreateWorkspaceModalOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [theme, setTheme] = useState<'light' | 'dark'>('light');
  const [teamMembers] = useState<any[]>([]);

  const showToast = useCallback((msg: string, _type?: string) => {
    setToastMessage(msg);
    setTimeout(() => {
      setToastMessage(null);
    }, 3500);
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme(prev => {
      const nextTheme = prev === 'light' ? 'dark' : 'light';
      if (nextTheme === 'dark') {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
      return nextTheme;
    });
  }, []);

  const value = useMemo(() => ({
    currentView,
    setCurrentView,
    activeWorkspace,
    setActiveWorkspace,
    workspaces,
    setWorkspaces,
    user,
    setUser,
    dbUser,
    setDbUser,
    loading,
    globalFilters,
    setGlobalFilters,
    isCreateWorkspaceModalOpen,
    setIsCreateWorkspaceModalOpen,
    toastMessage,
    showToast,
    theme,
    toggleTheme,
    teamMembers
  }), [
    currentView,
    activeWorkspace,
    workspaces,
    user,
    dbUser,
    loading,
    globalFilters,
    isCreateWorkspaceModalOpen,
    toastMessage,
    showToast,
    theme,
    toggleTheme,
    teamMembers
  ]);

  return (
    <UIContext.Provider value={value}>
      {children}
      {toastMessage && (
        <div className="fixed bottom-6 right-6 z-50 bg-[#111111] text-white px-5 py-3 rounded-xl shadow-2xl flex items-center gap-3 border border-white/10 animate-fade-in">
          <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
          <span className="text-sm font-medium">{toastMessage}</span>
        </div>
      )}
    </UIContext.Provider>
  );
}

export function useUI() {
  const context = useContext(UIContext);
  if (!context) {
    throw new Error('useUI must be used within a UIProvider');
  }
  return context;
}
